/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
	ClientUserGeneratedContentFlag,
	ClientUserGeneratedContentFlagChat,
	ClientUserGeneratedContentFlagComment,
	ClientUserGeneratedContentFlagDetail,
	ClientUserGeneratedContentFlagGroup,
	ClientUserGeneratedContentFlagPost,
	ClientUserGeneratedContentFlagStatusRecord
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { BaseError } from '../../../shared/errors/Errors';
import { INCLUDED_STATUS_MAPPING_UNRESOLVED, IncludedStatusMapping } from '../services/FlaggedContent.service';
import { TableColumnSortingDirection, TableSorting } from '../../../shared/views/Table/TableSorting';
import { Pagination } from '../../../shared/services/InMemoryTable.service';
import { FlagSummary } from '../flagged-content-details/shared/FlaggedContentDetailsCommons';

export enum FlaggedType {
	POST = 'POST',
	COMMENT = 'COMMENT',
	CHAT = 'CHAT',
	GROUP = 'GROUP',
	OTHER = 'OTHER'
}

export interface ListSearchParams {
	includedStatus: IncludedStatusMapping;
	sorting: TableSorting;
	pagination: Pagination;
}

export interface FlaggedContentState {
	list: {
		flaggedContents: ClientUserGeneratedContentFlag[];
		searchParams: ListSearchParams;
		loading: boolean;
		error: BaseError | null;
	};
	detail: {
		type: FlaggedType;
		post: ClientUserGeneratedContentFlagPost | undefined;
		chat: ClientUserGeneratedContentFlagChat | undefined;
		comment: ClientUserGeneratedContentFlagComment | undefined;
		group: ClientUserGeneratedContentFlagGroup | undefined;
		other: ClientUserGeneratedContentFlagDetail | undefined;
		flagId: string;
		loading: boolean;
		statusRecords: ClientUserGeneratedContentFlagStatusRecord[];
		flagSummary: FlagSummary;
	};
}

export const initialFlaggedContentState: FlaggedContentState = {
	list: {
		flaggedContents: [],
		searchParams: {
			includedStatus: INCLUDED_STATUS_MAPPING_UNRESOLVED,
			sorting: {
				columnId: 'lastStatusUpdate',
				direction: TableColumnSortingDirection.DESC
			},
			pagination: {
				page: 0,
				rowsPerPage: 20,
				total: 0
			}
		},
		loading: false,
		error: null
	},
	detail: {
		type: FlaggedType.OTHER,
		post: undefined,
		chat: undefined,
		comment: undefined,
		group: undefined,
		other: undefined,
		flagId: '',
		loading: false,
		statusRecords: [],
		flagSummary: {
			text: undefined,
			tenantName: undefined,
			reason: undefined,
			status: undefined,
			lastStatusUpdate: undefined,
			highlightedCommentId: undefined,
			flagCreator: undefined
		}
	}
};
